import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { saveAccountNotificationPreferences } from '@/app/network/firefly-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { Description, Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { Switch, SwitchField } from '@/common/components/switch';
import { Text } from '@/common/components/text';
import { billingCategories, outageCategories } from '@/notifications/notification-preferences';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

export const Route = createFileRoute('/wizard/memberships/$member-number/notifications')({
  component: NotificationsPage
});

const selectedDeliveryMethods = ['email', 'push'];

function NotificationsPage() {
  const [billing, setBilling] = useState(true);
  const [outages, setOutages] = useState(true);

  const { membership } = Route.useRouteContext();
  const { accounts, id } = useCurrentUser();
  const membershipAccounts = accounts.filter(account => account.member_number === membership.member_number);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync } = useMutation({
    mutationFn: async () => {
      const billingPreferences = billingCategories.map(category => ({
        notification_category: category,
        selected_delivery_methods: billing ? selectedDeliveryMethods : []
      }));

      const outagePreferences = outageCategories.map(category => ({
        notification_category: category,
        selected_delivery_methods: outages ? selectedDeliveryMethods : []
      }));

      const preferences = billingPreferences.concat(outagePreferences);

      await Promise.all(
        membershipAccounts.map(account => saveAccountNotificationPreferences(
          id,
          account.account_number,
          preferences
        ))
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      await navigate({ to: '/wizard/memberships/$member-number/tap', params: { 'member-number': membership.member_number.toString() } });
    },
    onError: error => handleUnexpectedError(error)
  });

  const handleSubmit = async () => {
    await mutateAsync();
  };

  return (
    <>
      <Heading>Notifications</Heading>
      <Text>Would you like to receive notifications regarding outages and other important alerts?</Text>

      <form className="grid w-full grid-cols-1 gap-8">
        <SwitchField>
          <Label>Billing</Label>
          <Description>Get notified of billing notices related to your account(s).</Description>
          <Switch
            checked={billing}
            onChange={() => setBilling(!billing)}
          />
        </SwitchField>
        <SwitchField>
          <Label>Outages</Label>
          <Description>Get notified of service outages and restorations related to your account(s).</Description>
          <Switch
            checked={outages}
            onChange={() => setOutages(!outages)}
          />
        </SwitchField>
        <ButtonGroup>
          <Button onClick={handleSubmit}>Continue</Button>
        </ButtonGroup>
      </form>
    </>
  );
}
